import {
    HIDE_CSS_CLASS,
    CLICK_EVENT,
} from '../JobFilter/constants';
import JobFilter from '../JobFilter/job-filter';
import Utils from '../JobFilter/Utils';
import StickyFilterBtn from './StickyFilterBtn';
import MasonryUtils from '../masonry-utils';

const JOB_DISPLAY_TYPES = {
    list: 'LIST',
    gitter: 'GITTER',
};

export default class JobFilterOverview {
    constructor(elm) {
        this.elm = elm;
        this.page = 1;
        this.pageSize = 10;
        this.currentJobs = [];
        this.jobsDisplayType = JOB_DISPLAY_TYPES.gitter;
        this.pageStartNode = elm.querySelector('[data-page-start]');
        this.pageEndNode = elm.querySelector('[data-page-end]');
        this.jobsDisplayNode = elm.querySelector('[data-job-list-view]');
        this.gitterTemplate = this.jobsDisplayNode.querySelector(
            '[data-gitter-template]',
        );
        this.displayGitterBtnNode = elm.querySelector('[data-list-gitter-btn]');
        this.displayListBtnNode = elm.querySelector('[data-list-btn]');
        this.listTemplate = this.jobsDisplayNode.querySelector(
            '[data-list-template]',
        );
        this.resultOverviewNode = elm.querySelector('[data-result-overview]');
        this.loadingJobsSpinnerNode = elm.querySelector(
            '[data-load-jobs-spinner]',
        );
        this.fetchMoreSectionNode = elm.querySelector(
            '[data-fetch-more-section]',
        );
        this.fetchMoreSpinnerNode = this.fetchMoreSectionNode?.querySelector(
            '[data-fetch-more-spinner]',
        );
        this.fetchMoreBtn = this.fetchMoreSectionNode?.querySelector('button');

        this.templates = {};
        this.msonryUtils = new MasonryUtils();

        this.utils = new Utils({
            onLoadingCallbacks: [
                this.toggleLoadingJobsSpinner.bind(this),
                this.toggleFetchMoreSpinner.bind(this),
            ],
            getPagination: this.getPagination.bind(this),
        });
        this.utils.addCallbackToOnFetchResult(
            this.onFetchJobsResult.bind(this),
        );

        this.stickyFilterElm = elm.querySelector('[data-sticky-filter-btn]');

        this.jobFilterElm = elm.querySelector('[data-job-filter]');
        this.jobFilters = new JobFilter(this.jobFilterElm, {
            utils: this.utils,
            getPagination: this.getPagination.bind(this),
            setPagination: this.setPagination.bind(this),
            onDisplayMobileMenu: function () {
                this.stickyFilterElm.classList.add('hide');
            }.bind(this),
            onCloseMobileMenu: function () {
                this.stickyFilterElm.classList.remove('hide');
            }.bind(this),
            onReset: this.onResetFilters.bind(this),
            onSearchBtnClick: function () {
                if (this.currentJobs?.length < 1) {
                    this.utils.fetchQuery();
                }
                this.scrollToResultSection();
            }.bind(this),
            fetchMoreBtn: this.fetchMoreBtn,
        });
        this.setPageNodes();

        this.stickyFilterBtn = new StickyFilterBtn(this.stickyFilterElm, {
            onClickFilter: this.jobFilters.displayMobileMenu.bind(
                this.jobFilters,
            ),
            onClickChangeDisplay: this.toggleJobDisplayType.bind(this),
        });
        this.jobFilters.setOnChangeFiltersCallback(
            function () {
                this.stickyFilterBtn.updateFilterBadgeNumber.bind(
                    this.stickyFilterBtn,
                );
                this.setPagination();
            }.bind(this),
        );

        this.displayListBtnNode?.addEventListener(
            CLICK_EVENT,
            function () {
                this.changeJobDisplayType(JOB_DISPLAY_TYPES.list);
            }.bind(this),
        );

        this.displayGitterBtnNode?.addEventListener(
            CLICK_EVENT,
            function () {
                this.changeJobDisplayType(JOB_DISPLAY_TYPES.gitter);
            }.bind(this),
        );
    }

    scrollToResultSection() {
        this.resultOverviewNode.scrollIntoView({ behavior: 'smooth' });
    }

    toggleNode(node, hide) {
        if (node) {
            const containsHideClass = node.classList.contains(HIDE_CSS_CLASS);
            if (hide) {
                !containsHideClass && node.classList.add(HIDE_CSS_CLASS);
            } else {
                containsHideClass && node.classList.remove(HIDE_CSS_CLASS);
            }
        }
    }

    toggleFetchMoreSpinner(display = true) {
        this.toggleNode(this.fetchMoreSpinnerNode, !display);
    }

    toggleLoadingJobsSpinner(display = true) {
        this.toggleNode(this.loadingJobsSpinnerNode, !display);
    }

    toggleJobDisplayType() {
        const displayType =
            this.jobsDisplayType !== JOB_DISPLAY_TYPES.gitter
                ? JOB_DISPLAY_TYPES.gitter
                : JOB_DISPLAY_TYPES.list;
        this.changeJobDisplayType(displayType);
    }

    changeJobDisplayType(type) {
        if (this.jobsDisplayType === type) {
            return;
        }
        this.jobsDisplayType = type;
        this.addJobsToDOM({ jobsList: this.currentJobs, reset: true });
        if (type === JOB_DISPLAY_TYPES.gitter) {
            this.jobsDisplayNode.classList.remove('row');
            this.displayListBtnNode.classList.remove('active');
            this.displayGitterBtnNode.classList.add('active');
            this.msonryUtils.layout();
        } else {
            this.msonryUtils.reset();
            this.jobsDisplayNode.classList.add('row');
            this.displayGitterBtnNode.classList.remove('active');
            this.displayListBtnNode.classList.add('active');
        }
    }

    toggleResultOverview({ hide = false } = {}) {
        this.toggleNode(this.resultOverviewNode, hide);
    }

    toggleFetchMoreSection({ hide = false } = {}) {
        this.toggleNode(this.fetchMoreSectionNode, hide);
    }

    setPageNodes(start = 0, end = 0) {
        if (this.pageStartNode && this.pageEndNode) {
            this.pageStartNode.textContent = start;
            this.pageEndNode.textContent = end;
        }
    }

    setPagination(page = 1, pageSize = 10) {
        this.page = page;
        this.pageSize = pageSize;
    }

    getPagination() {
        return { page: this.page, pageSize: this.pageSize };
    }

    addJobsToDOM({ jobsList = [], reset = false, addToMasonry = false }) {
        if (reset) {
            this.jobsDisplayNode.textContent = '';
        }
        // TODO: merge list/gitter templates to a single component,
        // that can be changed by a css class, to avoid too many requests for images, when changing the view/display
        const fragment = document.createDocumentFragment();
        const jobNodes = jobsList?.map((job) =>
            fragment.appendChild(this.getTemplateNode(job)),
        );
        this.jobsDisplayNode.appendChild(fragment);
        if (addToMasonry) {
            this.msonryUtils.appendNodes(jobNodes);
            this.msonryUtils.reloadLayout();
        }
    }

    onResetFilters() {
        this.setPageNodes();
        this.jobsDisplayNode.textContent = '';
        this.toggleFetchMoreSection({ hide: true });
        this.toggleResultOverview({ hide: true });
        this.currentJobs = [];
        this.msonryUtils.reset();
    }

    onFetchJobsResult(fetchResult, fetchMore = false) {
        try {
            const { itemsPerPage = 0, totalItems = 0, jobs = [] } = fetchResult;
            const pageItems = itemsPerPage * this.page;
            this.setPageNodes(
                totalItems <= pageItems ? totalItems : pageItems,
                totalItems,
            );
            const newJobsToAdd = jobs.map((job) => {
                this.currentJobs.push(job);
                return job;
            });

            this.toggleResultOverview();
            this.addJobsToDOM({
                jobsList: newJobsToAdd,
                reset: !fetchMore,
                addToMasonry: this.jobsDisplayType === JOB_DISPLAY_TYPES.gitter,
            });
            this.toggleFetchMoreSection({
                hide: totalItems <= pageItems,
            });
        } catch (error) {
            console.error(error);
        }
    }

    getTemplateNode({
        jobNumber,
        jobType,
        jobTitle,
        jobTeaser,
        location: { zipCodes },
        company: { isAnonymous, name: companyName, logoUrl },
    }) {
        const template = document.importNode(
            (this.jobsDisplayType === JOB_DISPLAY_TYPES.gitter
                ? this.gitterTemplate
                : this.listTemplate
            ).content,
            true,
        );
        const companyTitleNode = template.querySelector('[data-company-title]');
        const jobTitleNode = template.querySelector('[data-job-title]');
        const regionNode = template.querySelector('[data-region]');
        const jobTypeNode = template.querySelector('[data-job-type]');
        const companyLogoNode = template.querySelector('[data-logo]');
        const jobTeaserNode = template.querySelector('[data-description]');
        const jobLinkBtn = template.querySelector('[data-job-link]');
        companyTitleNode.textContent = isAnonymous ? '' : companyName;
        companyLogoNode.src = isAnonymous ? '' : logoUrl;
        jobTitleNode.textContent = jobTitle;
        regionNode.textContent = zipCodes?.length > 0 ? zipCodes[0].region : '';
        jobTypeNode.textContent = jobType?.length > 0 ? jobType[0].jobType : '';
        jobTeaserNode.textContent = jobTeaser;
        jobLinkBtn.href = jobLinkBtn.href + jobNumber;

        return template;
    }
}
